import React, { useState } from "react";
import { useSelector } from "react-redux";
import clsx from "clsx";
import differenceInDays from "date-fns/differenceInDays";
import format from "date-fns/format";
import startOfToday from "date-fns/startOfToday";

import Button from "@material-ui/core/Button";
import FormControl from "@material-ui/core/FormControl";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Radio from "@material-ui/core/Radio";

import Calendar from "components/DateRangeCalendar";
import Loader from "components/Loader";
import { useSnackbar } from "components/SnackbarContext/SnackbarContext";
import { useApiClient } from "hooks/api";
import VetWorkoutsPageState from "interfaces/VetWorkoutsPageState";
import { currentTimeZoneString } from "utils/date-utils";
import { IDateRange } from "utils/facilityBlackouts";

import {
  DateRangeType,
  downloadFile,
  getDateOptions,
  ReportType
} from "./helper";
import useStyles from "./styles";

interface IProps {
  open: boolean;
  onClose: () => void;
}

const WorkoutRequestReportDialog = React.memo((props: IProps) => {
  const classes = useStyles();
  const [selectedReportType, setSelectedReportType] = useState<ReportType>(
    ReportType.SCHEDULED_WORKOUTS
  );
  const [selectedDateRangeType, setSelectedDateRangeType] =
    useState<DateRangeType>(DateRangeType.TODAY);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [showCalendar, setShowCalendar] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date>(null);
  const [endDate, setEndDate] = useState<Date>(null);
  const { selectedFacility } = useSelector(
    (state: { vetWorkoutsPage: VetWorkoutsPageState }) => state?.vetWorkoutsPage
  );

  const { open, onClose } = props;

  const { horseExamClient, workoutRequestClient } = useApiClient();

  const { showSuccessSnack, showErrorSnack } = useSnackbar();

  const selectedDateRange = Number(selectedDateRangeType);
  const relativeDateRange =
    selectedDateRangeType === DateRangeType.CUSTOM
      ? {
          startDate: {
            value: differenceInDays(startDate, startOfToday())
          },
          endDate: {
            value: differenceInDays(endDate, startOfToday()) + 1
          }
        }
      : {
          startDate: {
            value: selectedDateRange < 0 ? selectedDateRange : 0
          },
          endDate: {
            value: selectedDateRange > 0 ? selectedDateRange : 1
          }
        };
  const query = {
    facilityIds: [selectedFacility?.id],
    dateRange: {
      range: {
        relative: {
          ...relativeDateRange,
          timezone: currentTimeZoneString()
        }
      }
    }
  };

  const downloadReport = downloadFn => {
    setIsDownloading(true);
    downloadFn(query)
      .then(result => {
        downloadFile(
          result.file,
          selectedReportType.toLowerCase().replace(" ", "_") + ".csv"
        );
        showSuccessSnack(
          `${selectedReportType} report downloaded successfully`
        );
        setIsDownloading(false);
        onClose();
      })
      .catch(error => {
        showErrorSnack(
          `${selectedReportType} report download failed: ${error.toString()}`
        );
        setIsDownloading(false);
      });
  };

  const handleReportTypeChange = event => {
    setSelectedReportType(event.target.value);
    if (
      [selectedReportType, event.target.value].includes(
        ReportType.SCHEDULED_WORKOUTS
      )
    ) {
      setSelectedDateRangeType(getDateOptions(event.target.value)[0]);
    }
  };

  const handleDateRangeSelectChange = event => {
    setSelectedDateRangeType(event.target.value);
    if (
      String(event.target.value) === String(DateRangeType.CUSTOM) &&
      (!startDate || !endDate)
    ) {
      setShowCalendar(true);
    }
  };

  const handleGoBack = () => {
    onClose();
  };

  const handleDone = () => {
    setShowCalendar(false);
    setSelectedDateRangeType(DateRangeType.CUSTOM);
  };

  const handleDownload = () => {
    switch (selectedReportType) {
      case ReportType.SCHEDULED_WORKOUTS: {
        downloadReport(workoutRequestClient.pullScheduledWorkoutReport);
        break;
      }
      case ReportType.PAST_EXAMS: {
        downloadReport(horseExamClient.pullHorseExamReport);
        break;
      }
      case ReportType.WORKOUT_REQUESTS: {
        downloadReport(workoutRequestClient.pullWorkoutRequestReport);
        break;
      }
      case ReportType.WRONGFUL_WORKOUTS: {
        downloadReport(workoutRequestClient.pullWrongfulWorkoutReport);
        break;
      }
    }
  };

  const handleSelectDateRangeClick = () => {
    setShowCalendar(true);
  };

  const handleDateRangeChange = (range: IDateRange) => {
    setStartDate(range.startDate);
    setEndDate(range.endDate);
  };

  const renderLoader = () => {
    return isDownloading && <Loader className={classes.loaderDownloading} />;
  };

  const renderReportTypeSection = () => {
    return (
      <div>
        <div className={classes.dialogContentExtraPadding}>Report Type</div>
        <FormControl
          className={clsx(
            classes.reportTypeForm,
            classes.dialogContentExtraPadding
          )}
          variant="outlined"
          fullWidth
        >
          <Select
            className={classes.reportTypeSelect}
            value={selectedReportType}
            MenuProps={{
              classes: {
                list: classes.selectList,
                paper: classes.selectPaper
              },
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left"
              },
              getContentAnchorEl: null
            }}
            onChange={handleReportTypeChange}
          >
            <MenuItem value={ReportType.SCHEDULED_WORKOUTS}>
              Scheduled Workouts
            </MenuItem>
            <MenuItem value={ReportType.PAST_EXAMS}>Past Exams</MenuItem>
            <MenuItem value={ReportType.WORKOUT_REQUESTS}>
              Workout Requests
            </MenuItem>
            <MenuItem value={ReportType.WRONGFUL_WORKOUTS}>
              Wrongful Workouts
            </MenuItem>
          </Select>
        </FormControl>
      </div>
    );
  };

  const renderDateOptionLabel = (dateRangeType: DateRangeType): string => {
    if (Number.isInteger(dateRangeType)) {
      if (dateRangeType === 0) {
        return "Today";
      }
      return `${dateRangeType > 0 ? "Next" : "Last"} ${Math.abs(
        Number(dateRangeType)
      )} Days`;
    }
    return "Custom Date Range";
  };

  const renderDateRangeTypeSection = () => {
    return (
      <div>
        <div className={classes.dialogContentExtraPadding}>Date Range</div>
        {getDateOptions(selectedReportType).map((enumVal: DateRangeType) => {
          return (
            <label key={enumVal} className={classes.dateRangeItem}>
              <Radio
                className={classes.radio}
                value={enumVal}
                checked={String(selectedDateRangeType) === String(enumVal)}
                onChange={handleDateRangeSelectChange}
              />
              <span className={classes.dateRangeText}>
                {renderDateOptionLabel(enumVal)}
              </span>
              {enumVal === DateRangeType.CUSTOM && (
                <Button
                  className={classes.dateRangeSelectButton}
                  variant={"text"}
                  onClick={handleSelectDateRangeClick}
                >
                  {startDate || endDate
                    ? `${startDate ? format(startDate, "M/d/yy") : "N/A"} - ${
                        endDate ? format(endDate, "M/d/yy") : "N/A"
                      }`
                    : "Select Date Range"}
                </Button>
              )}
            </label>
          );
        })}
      </div>
    );
  };

  return (
    <Dialog
      className={clsx({ [classes.dialogCalendar]: showCalendar })}
      classes={{ paper: classes.dialog }}
      open={open}
      onClose={onClose}
    >
      <DialogTitle className={classes.dialogTitle}>
        {showCalendar ? "Select Date Range" : "Workout Request Report"}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {showCalendar ? (
          <div>
            <Calendar
              onDateRangeChange={handleDateRangeChange}
              defaultRange={{
                startDate,
                endDate
              }}
              showCalendar
              previewFormat={"M/d/yyyy"}
              classes={{
                dates: classes.calendarDates
              }}
            />
          </div>
        ) : (
          <>
            <div className={classes.dialogContentExtraPadding}>
              Select a report type and date range to download .csv report.
            </div>
            {renderReportTypeSection()}
            {renderDateRangeTypeSection()}
          </>
        )}
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <div className={classes.buttons}>
          <Button
            className={clsx(classes.button, classes.goBackButton)}
            onClick={handleGoBack}
          >
            Go Back
          </Button>
          {showCalendar ? (
            <Button
              className={clsx(classes.button, classes.downloadButton)}
              onClick={handleDone}
            >
              DONE
            </Button>
          ) : (
            <Button
              className={clsx(classes.button, classes.downloadButton)}
              onClick={handleDownload}
              disabled={
                selectedDateRangeType === DateRangeType.CUSTOM
                  ? !startDate || !endDate
                  : false
              }
            >
              Download
            </Button>
          )}
        </div>
      </DialogActions>
      {renderLoader()}
    </Dialog>
  );
});

export default WorkoutRequestReportDialog;
